import React, { useEffect, useState } from 'react';
import { Modal, Input, Button, Alert } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { GoogleAuthProvider, OAuthProvider, onAuthStateChanged, signInWithPopup } from 'firebase/auth';
import { auth } from '../../../js/firebase';

// "Confirm it's you" before something sensitive (creating an access token,
// connecting an AI app). The backend never trusts the session alone for these:
// it needs either the account password or a FRESH Google/Apple ID token for
// the same email (see StepUpService on the backend).
//
// Props:
//   open, onCancel
//   onConfirm(credential) -> Promise<{ ok: boolean, message?: string }>
//     credential is { password } or { firebaseIdToken }
//   methods: ['sso', 'password'] (default) or ['sso'] for Google/Apple accounts
//   accountEmail: shown, and used to offer a one-click confirm when this
//     browser is still signed in to Google/Apple with the same account
//   title, description: optional overrides
const providerIconStyle = { width: 16, height: 16, display: 'inline-block' };

const StepUpModal = ({ open, onCancel, onConfirm, methods = ['sso', 'password'], accountEmail, title, description }) => {
  const intl = useIntl();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [firebaseUser, setFirebaseUser] = useState(auth.currentUser);

  useEffect(() => onAuthStateChanged(auth, setFirebaseUser), []);
  useEffect(() => {
    if (!open) {
      setPassword('');
      setError('');
      setBusy(false);
    }
  }, [open]);

  const allowPassword = methods.includes('password');
  const sameFirebaseAccount =
    firebaseUser?.email && accountEmail && firebaseUser.email.toLowerCase() === accountEmail.toLowerCase();

  const submit = async (credential) => {
    setBusy(true);
    setError('');
    try {
      const result = await onConfirm(credential);
      if (!result?.ok) {
        setError(result?.message || intl.formatMessage({ id: 'STEP_UP_FAILED', defaultMessage: 'We could not confirm it is you. Please try again.' }));
      }
    } finally {
      setBusy(false);
    }
  };

  // Silent: the browser is still signed in to Google/Apple (Firebase) as the
  // same account - force-refresh the ID token so it is freshly issued.
  const confirmWithCurrentSso = async () => {
    try {
      const idToken = await firebaseUser.getIdToken(true);
      await submit({ firebaseIdToken: idToken });
    } catch (e) {
      setError(intl.formatMessage({ id: 'STEP_UP_SSO_FAILED', defaultMessage: 'Sign-in failed. Please try again.' }));
    }
  };

  const confirmWithProvider = async (provider) => {
    setError('');
    try {
      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken(true);
      await submit({ firebaseIdToken: idToken });
    } catch (e) {
      if (e?.code !== 'auth/popup-closed-by-user' && e?.code !== 'auth/cancelled-popup-request') {
        setError(intl.formatMessage({ id: 'STEP_UP_SSO_FAILED', defaultMessage: 'Sign-in failed. Please try again.' }));
      }
    }
  };

  const googleProvider = () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account', ...(accountEmail ? { login_hint: accountEmail } : {}) });
    return provider;
  };

  return (
    <Modal
      open={open}
      onCancel={busy ? undefined : onCancel}
      footer={null}
      destroyOnClose
      title={title || <FormattedMessage id="STEP_UP_TITLE" defaultMessage="Confirm it's you" />}
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-gray-600">
          {description || (
            <FormattedMessage
              id="STEP_UP_DESCRIPTION"
              defaultMessage="For your security, confirm your identity to continue."
            />
          )}
          {accountEmail && (
            <>
              {' '}
              <FormattedMessage
                id="STEP_UP_ACCOUNT"
                defaultMessage="Account: {email}"
                values={{ email: <strong>{accountEmail}</strong> }}
              />
            </>
          )}
        </p>

        {error && <Alert type="error" showIcon message={error} />}

        {sameFirebaseAccount && (
          <Button type="primary" block loading={busy} onClick={confirmWithCurrentSso}>
            <FormattedMessage
              id="STEP_UP_CONTINUE_AS"
              defaultMessage="Continue as {email}"
              values={{ email: firebaseUser.email }}
            />
          </Button>
        )}
        {/* Icons via antd's `icon` prop: antd forces `> span { display:
            inline-block }` on button children, which broke a hand-made
            inline-flex row (icon wrapped above the text, overflowing). */}
        <Button
          block
          disabled={busy}
          icon={<img src="/images/icons/google-logo.svg" alt="" style={providerIconStyle} />}
          onClick={() => confirmWithProvider(googleProvider())}
        >
          <FormattedMessage id="CONTINUE_GOOGLE" defaultMessage="Continue with Google" />
        </Button>
        <Button
          block
          disabled={busy}
          icon={<img src="/images/icons/apple-logo.svg" alt="" style={providerIconStyle} />}
          onClick={() => confirmWithProvider(new OAuthProvider('apple.com'))}
        >
          <FormattedMessage id="CONTINUE_APPLE" defaultMessage="Continue with Apple" />
        </Button>

        {allowPassword && (
          <form
            className="flex flex-col gap-2 pt-2 border-t border-gray-100"
            onSubmit={(e) => {
              e.preventDefault();
              if (password) submit({ password });
            }}
          >
            <label className="text-xs text-gray-500" htmlFor="step-up-password">
              <FormattedMessage id="STEP_UP_OR_PASSWORD" defaultMessage="Or enter your Workflo password" />
            </label>
            <Input.Password
              id="step-up-password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
            <Button type={sameFirebaseAccount ? 'default' : 'primary'} htmlType="submit" loading={busy} disabled={!password}>
              <FormattedMessage id="STEP_UP_CONFIRM" defaultMessage="Confirm" />
            </Button>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default StepUpModal;
